import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConsentStore } from '../stores/consentStore';

export default function Footer() {
  const { t } = useTranslation();
  const resetConsent = useConsentStore((s) => s.resetConsent);

  const platformLinks = [
    { to: '/dashboard', labelKey: 'nav.dashboard' },
    { to: '/study', labelKey: 'nav.study' },
    { to: '/exam', labelKey: 'nav.exam' },
  ] as const;

  const companyLinks = [
    { to: '/privacy', labelKey: 'footer.privacy' },
    { to: '/mentions-legales', labelKey: 'footer.legalNotice' },
  ] as const;

  const legalLinks = [
    { to: '/terms', labelKey: 'footer.termsOfService' },
    { to: '/cgv', labelKey: 'footer.termsOfSale' },
  ] as const;

  return (
    <footer className="border-t border-slate-200 bg-white dark:border-[rgba(255,255,255,0.1)] dark:bg-transparent print:hidden">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Grid sections */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:grid-cols-4">
          {/* Brand column */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1 mb-4 lg:mb-0">
            <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
                </svg>
              </div>
              <span><span className="text-slate-700 dark:text-slate-200">Cloud</span><span className="text-blue-600 dark:text-blue-400">Master</span></span>
            </div>
            <p className="text-xs text-slate-400 dark:text-[#64748B] max-w-xs leading-relaxed">
              {t('footer.tagline')}
            </p>
            {/* Status indicator */}
            <div className="mt-4 flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500"></span>
              </span>
              <span className="text-xs text-slate-400 dark:text-[#64748B]">{t('footer.allSystemsOperational')}</span>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-[#94A3B8] mb-3">{t('footer.platform')}</h3>
            <nav className="flex flex-col gap-2" aria-label={t('footer.platformLinks')}>
              {platformLinks.map(({ to, labelKey }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-[#64748B] dark:hover:text-white"
                >
                  {t(labelKey)}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={resetConsent}
                className="text-left text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-[#64748B] dark:hover:text-white"
              >
                {t('footer.cookiePreferences')}
              </button>
            </nav>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-[#94A3B8] mb-3">{t('footer.company')}</h3>
            <nav className="flex flex-col gap-2" aria-label={t('footer.companyLinks')}>
              {companyLinks.map(({ to, labelKey }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-[#64748B] dark:hover:text-white"
                >
                  {t(labelKey)}
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-[#94A3B8] mb-3">{t('footer.legal')}</h3>
            <nav className="flex flex-col gap-2" aria-label={t('footer.legalLinks')}>
              {legalLinks.map(({ to, labelKey }) => (
                <NavLink
                  key={to}
                  to={to}
                  className="text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-[#64748B] dark:hover:text-white"
                >
                  {t(labelKey)}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-[rgba(255,255,255,0.08)]">
          <p className="text-xs text-slate-400 dark:text-[#64748B] text-center">
            {t('footer.copyright', { year: new Date().getFullYear(), version: __APP_VERSION__ })}
          </p>
        </div>
      </div>
    </footer>
  );
}
